/** Locale-aware formatting (WP-03.4): fr-CD for French, ln-CD for Lingala when the runtime has it (fr-CD otherwise), en-GB for English; CDF figures without decimals. */

export type Status = "Green" | "Amber" | "Red" | "Grey";

export const STATUS_ORDER: Record<Status, number> = { Red: 0, Amber: 1, Green: 2, Grey: 3 };

const LN_LOCALE: string = (() => {
  try { return Intl.NumberFormat.supportedLocalesOf(["ln-CD"]).length ? "ln-CD" : "fr-CD"; } catch { return "fr-CD"; }
})();

export function sameLanguage(stored: string | null | undefined, lng: string): boolean {
  return ((stored || "fr").slice(0, 2)) === (lng || "fr").slice(0, 2);
}

export function localeFor(lng: string | undefined): string {
  if (!lng) return "fr-CD";
  if (lng.startsWith("en")) return "en-GB";
  if (lng.startsWith("ln")) return LN_LOCALE;
  return "fr-CD";
}

/** Relative deviation from target in percent; null when there is no usable target. */
export function deviationPct(value: number | null | undefined, target: number | null | undefined): number | null {
  if (value == null || target == null || target === 0) return null;
  return ((value - target) / target) * 100;
}

export function fmtNumber(v: number | null | undefined, lng: string, digits = 1): string {
  if (v == null || Number.isNaN(v)) return "–";
  return new Intl.NumberFormat(localeFor(lng), { maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(v);
}

export function fmtPct(v: number | null | undefined, lng: string, digits = 1, signed = true): string {
  if (v == null || Number.isNaN(v)) return "–";
  const sign = signed && v > 0 ? "+" : "";
  return `${sign}${fmtNumber(v, lng, digits)} %`;
}

/** Value with its KPI unit: "%" → "36,3 %", "CDF m" → "47 900 M CDF", "days" → "58 days" (unit label translated by caller). */
export function fmtValue(v: number | null | undefined, unit: string | null | undefined, lng: string, unitLabel?: string): string {
  if (v == null) return "–";
  const u = (unit ?? "").trim();
  if (u === "%") return `${fmtNumber(v, lng, 1)} %`;
  if (u.toUpperCase() === "CDF M") return `${fmtNumber(v, lng, 0)} M CDF`;
  if (u.toUpperCase() === "CDF") return new Intl.NumberFormat(localeFor(lng), { style: "currency", currency: "CDF", maximumFractionDigits: 0 }).format(v);
  if (u.toUpperCase() === "USD") return new Intl.NumberFormat(localeFor(lng), { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v);
  const label = unitLabel ?? u;
  return label ? `${fmtNumber(v, lng, Number.isInteger(v) ? 0 : 1)} ${label}` : fmtNumber(v, lng, 1);
}

/** UTC offset of the ERPNext site time zone (Africa/Kinshasa, no DST). Frappe returns naive datetimes in that zone. */
export const SITE_UTC_OFFSET: string = (import.meta.env.VITE_SITE_UTC_OFFSET as string | undefined) ?? "+01:00";

/** Frappe datetimes come as "YYYY-MM-DD HH:MM:SS.ffffff" in the site time zone (no offset in the string). */
export function parseFrappeDatetime(s: string | null | undefined, offset: string = SITE_UTC_OFFSET): Date | null {
  if (!s) return null;
  const m = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})(?:\.(\d{1,6}))?(Z|[+-]\d{2}:\d{2})?$/.exec(s.trim());
  if (!m) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const ms = (m[3] ?? "").padEnd(3, "0").slice(0, 3);
  const d = new Date(`${m[1]}T${m[2]}.${ms}${m[4] ?? offset}`);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function fmtRelative(date: Date | null, lng: string, now: number = Date.now()): string {
  if (!date) return "–";
  const diff = (date.getTime() - now) / 1000;
  const abs = Math.abs(diff);
  const rtf = new Intl.RelativeTimeFormat(localeFor(lng), { numeric: "auto" });
  if (abs < 60) return rtf.format(Math.round(diff), "second");
  if (abs < 3600) return rtf.format(Math.round(diff / 60), "minute");
  if (abs < 86400) return rtf.format(Math.round(diff / 3600), "hour");
  return rtf.format(Math.round(diff / 86400), "day");
}

/** "2026-08" → "août 2026" / "Aug 2026". Unknown shapes are returned unchanged. */
export function fmtPeriod(period: string, lng: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(period);
  if (!m) return period;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1));
  return new Intl.DateTimeFormat(localeFor(lng), { month: "short", year: "numeric", timeZone: "UTC" }).format(d);
}

/** ISO date n days from now (for action due dates). */
export function isoDatePlusDays(days: number, from: Date = new Date()): string {
  const d = new Date(from.getTime() + days * 86400000);
  return d.toISOString().slice(0, 10);
}

/** "7:00:00" or "07:00:00" (Frappe's Time values come both ways) → "07:00". */
export function hhmm(v: string | null | undefined): string {
  if (!v) return "";
  const [h, m = "00"] = v.split(":");
  return `${h.padStart(2, "0")}:${m.slice(0, 2).padStart(2, "0")}`;
}
